import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactFlow, { Controls, Background, useNodesState, useEdgesState } from 'reactflow';
import './OwnershipStructureDiagram.css';
import Button from '@mui/material/Button';
import { Box, Modal, Paper } from '@mui/material';
import { styles } from '../../styles.ts';
import 'reactflow/dist/style.css';
import { NodeCard } from '~/modules/identification/forms/OwnershipStructure/components/Diagram/NodeCard.tsx';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  TNodeCardData,
  TOwnerDiagramNode,
  TOwnerLink,
  TOwnershipStructure,
} from '~/modules/identification/forms/OwnershipStructure/types.ts';
import { buildTree } from '../../utils/utils.tsx';
import { TGeneralInfoLegalResident } from '~/modules/identification/forms/GeneralInfo/types.ts';
import type { Node } from 'reactflow';
import {
  buildFlowElements,
  checkIndividual,
  getLayoutedElements,
} from '~/modules/identification/forms/OwnershipStructure/utils/diagramUtils.tsx';
import { generateOwnershipStructurePdf } from '~/modules/identification/forms/OwnershipStructure/utils/documentDiagramUtils.tsx';
import { setDisabledDiagramGeneration } from '~/store/identification-form/slices.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  selectGeneralInfo,
  selectGettingStarted,
  selectManagmentStructure,
} from '~/store/identification-form/selectors.ts';

export default function OwnershipStructureDiagram() {
  const dispatch = useAppDispatch();
  const { fullNameUkr } = useSelector(selectGeneralInfo) as TGeneralInfoLegalResident;
  const { identificationNumber } = useSelector(selectGettingStarted);
  const { position, lastName, firstName, middleName } = useSelector(selectManagmentStructure);

  const clientFullInformation = [position?.label, lastName, firstName, middleName].join(' ').trim();

  const {
    control,
    formState: { errors, defaultValues },
  } = useFormContext<TOwnershipStructure>();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleModal = () => setIsModalOpen(false);

  const [owners, link] = useWatch({ control, name: ['owners', 'link'] });
  const tree = useMemo(() => buildTree(owners, link), [owners, link]) as TOwnerDiagramNode[];

  useEffect(() => {
    const [builtNodes, builtEdges] = buildFlowElements(
      tree,
      fullNameUkr,
      identificationNumber as string,
      link as TOwnerLink[]
    );
    const [layoutedNodes, layoutedEdges] = getLayoutedElements(builtNodes, builtEdges);
    setNodes(layoutedNodes as unknown as Node[]);
    setEdges(layoutedEdges);
  }, [tree, fullNameUkr, identificationNumber, link, setNodes, setEdges]);

  const updateNodesWithAddHandler = nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      label: <NodeCard data={node.data as TNodeCardData} />,
    },
  }));

  const handleExportPdf = async () => {
    if (isExporting) return;

    try {
      setIsExporting(true);

      const file = await generateOwnershipStructurePdf({
        owners,
        link,
        fullNameUkr,
        identificationNumber: identificationNumber!,
        clientFullInformation,
      });

      const url = URL.createObjectURL(file);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const isAllIndividual = useMemo(() => checkIndividual(tree), [tree]);
  const prevIsAllIndividualRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (prevIsAllIndividualRef.current !== isAllIndividual) {
      dispatch(setDisabledDiagramGeneration(isAllIndividual));
      prevIsAllIndividualRef.current = isAllIndividual;
    }
  }, [isAllIndividual, dispatch]);

  const isDisabledDiagramGeneration =
    Object.keys(errors).length > 0 || !defaultValues?.owners || isAllIndividual;

  return (
    <>
      <Box>
        <Button
          onClick={handleOpenModal}
          disabled={isDisabledDiagramGeneration}
          variant="outlined"
          size="large"
          data-testid="ownership-structure-chart"
        >
          Схема структури власності
        </Button>
      </Box>

      <Modal open={isModalOpen} onClose={handleModal}>
        <Paper sx={styles.diagramPaper}>
          <Box sx={styles.container}>
            <Box
              sx={{
                height: '100%',
                width: '100%',
                '& .react-flow__attribution': { display: 'none' },
              }}
              id="diagram-wrapper"
            >
              <ReactFlow
                nodes={updateNodesWithAddHandler}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodesDraggable={false}
                nodesConnectable={false}
                fitView
                minZoom={0.05}
                maxZoom={2}
              >
                {/*<MiniMap />*/}
                <Controls showInteractive={false} />
                <Background />
              </ReactFlow>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleExportPdf}
                disabled={isExporting}
                variant="outlined"
                size="large"
                data-testid="export-to-pdf"
              >
                Експортувати в PDF
              </Button>
              <Button onClick={handleModal} variant="contained" size="large" data-testid="continue">
                Продовжити
              </Button>
            </Box>
          </Box>
        </Paper>
      </Modal>
    </>
  );
}
